import { CollisionEventType, Component, Emitter, Object3D, PhysXComponent } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';

/**
 * TriggerCollision; emits an event when the other object collides with this object.
 */
export class TriggerCollision extends Component {
    static TypeName = 'trigger-collision';

    /**
     * The object that will cause the action to be triggered when it
     * collides with this object.
     */
    @property.object()
    otherObject?: Object3D;

    /**
     * If true, the action will only be triggered once.
     * After triggering, the physX component will be deactivated.
     * @default false
     */
    @property.bool(false)
    once = false;

    /**
     * Emits an event when the other object collides with this object.
     */
    onTrigger: Emitter = new Emitter();

    private physxComponent?: PhysXComponent;
    private targetPhysxComponent?: PhysXComponent;
    private hasTriggered = false;

    start() {
        if (!this.otherObject) {
            throw new Error(
                'TriggerAudio component requires a Target to be set to respond to.'
            );
        }

        const component = this.object.getComponent(PhysXComponent);
        if (!component) {
            throw new Error('TriggerAudio component requires a PhysXComponent to be set.');
        }
        this.physxComponent = component;

        const targetComponent = this.otherObject?.getComponent(PhysXComponent);
        if (!targetComponent) {
            throw new Error(
                'TriggerAudio component requires a Target PhysXComponent to be set.'
            );
        }
        this.targetPhysxComponent = targetComponent;

        this.physxComponent.onCollisionWith(
            this.targetPhysxComponent,
            this.onCollision.bind(this)
        );
    }

    onCollision(type: CollisionEventType, other: PhysXComponent) {
        if (this.hasTriggered && this.once) {
            return;
        }

        if (type !== CollisionEventType.TriggerTouch ||
            other.object !== this.otherObject) {
            return;
        }

        this.onTrigger.notify();

        if (this.once && this.physxComponent) {
            setTimeout(() => {
                this.physxComponent!.active = false;
            }, 100);
        }
        this.hasTriggered = true;
    }
}
